// SDK 运行时接口层。壳层会在启动时通过 `installRuntime` 注入实现；
// APP 只调用这里导出的函数，不直接接触壳内部实现。
// 这种间接层也是 T-3.3.3 中 ESLint 规则要强制保证的结构：APP 只能从
// @blade/os-sdk 导入能力。
import type { AxiosInstance } from 'axios';
import { createContext, useContext } from 'react';
import type { Permission } from './permissions';

export interface UserInfo {
  id: number;
  username: string;
  display_name: string;
  email: string | null;
  avatar_url: string | null;
  locale: string;
  must_change_password: boolean;
}

export interface NotificationPayload {
  message: string;
  level?: 'info' | 'success' | 'warning' | 'danger';
  key?: string;
  ttlMs?: number;
}

export interface WindowContextValue {
  windowId: string;
  params?: unknown;
}

export interface SdkRuntime {
  http: () => AxiosInstance;
  launchApp: (appId: string, options?: { params?: unknown }) => string | null;
  closeWindow: (windowId?: string) => void;
  notify: (payload: NotificationPayload) => void;
  currentUser: () => Readonly<UserInfo> | null;
  updateCurrentUser: (user: UserInfo) => void;
  switchLocale: (locale: string) => void;
  t: (key: string, params?: Record<string, unknown>) => string;
  hasPermission: (permission: Permission) => boolean;
  refreshRegistry: () => Promise<void>;
  // 让 APP 把本地 pending 桌面布局修改先 flush 到服务端,再 load 拉新布局。
  // App Installer 安装/卸载 App 后调用,确保新增/删除的 App 立刻反映在桌面;
  // 失败语义见 frontend/src/store/desktopLayoutStore.ts 的 flush 契约。
  refreshDesktopLayout: () => Promise<void>;
}

let _runtime: SdkRuntime | null = null;

export function installRuntime(runtime: SdkRuntime): void {
  _runtime = runtime;
}

export function resetRuntime(): void {
  _runtime = null;
}

function getRuntime(): SdkRuntime {
  if (!_runtime) throw new Error('SDK runtime not installed');
  return _runtime;
}

export function http(): AxiosInstance {
  return getRuntime().http();
}

export function launchApp(
  appId: string,
  options?: { params?: unknown },
): string | null {
  return getRuntime().launchApp(appId, options);
}

export function closeWindow(windowId?: string): void {
  getRuntime().closeWindow(windowId);
}

export function notify(payload: NotificationPayload): void {
  getRuntime().notify(payload);
}

export function currentUser(): Readonly<UserInfo> | null {
  return getRuntime().currentUser();
}

export function updateCurrentUser(user: UserInfo): void {
  getRuntime().updateCurrentUser(user);
}

export function switchLocale(locale: string): void {
  getRuntime().switchLocale(locale);
}

export function t(key: string, params?: Record<string, unknown>): string {
  return getRuntime().t(key, params);
}

export function hasPermission(permission: Permission): boolean {
  return getRuntime().hasPermission(permission);
}

export function refreshRegistry(): Promise<void> {
  return getRuntime().refreshRegistry();
}

export function refreshDesktopLayout(): Promise<void> {
  return getRuntime().refreshDesktopLayout();
}

export const WindowContext = createContext<WindowContextValue | null>(null);

export function useWindowContext(): WindowContextValue {
  const ctx = useContext(WindowContext);
  if (!ctx) throw new Error('useWindowContext requires a WindowContext provider');
  return ctx;
}
